/** @file NodeRow: one node-tree row (fold, name or rename field, badges, eye; drag to reparent). */
import type { CSSProperties, DragEvent, MouseEvent, ReactElement } from 'react';
import type { ClickMods } from './clickSelection';
import { NodeBadges } from './NodeBadges';
import { RenameField } from './RenameField';
import type { TreeRow } from './treeRows';

/** The tree's callbacks for a row. `onRename` resolves true when the name field may close. */
export type NodeRowActions = {
  onSelect: (name: string, mods: ClickMods) => void;
  onMenu: (name: string, x: number, y: number) => void;
  onToggle: (name: string) => void;
  onReparent: (name: string, parent: string) => void;
  onFrame: (name: string) => void;
  onEye: (name: string) => void;
  onRename: (name: string, value: string, viaBlur: boolean) => Promise<boolean>;
  onRenameCancel: () => void;
};

/** What the row shows: its data, selection, fold, drawables, key at the playhead, view-hidden and rename state. */
export type NodeRowView = {
  row: TreeRow; selected: boolean; collapsed: boolean; drawables: string[]; keyed: boolean; frame: number;
  viewHidden: boolean; viewHiddenAbove: boolean; renaming: boolean; renameError: string | null;
};

const DRAG_TYPE = 'application/x-pose3d-node';
const muted: CSSProperties = { color: 'var(--muted, #8b919e)', fontSize: 11 };

function rowStyle(v: NodeRowView): CSSProperties {
  const dim = v.row.hidden ? 0.5 : v.viewHidden ? 0.55 : v.viewHiddenAbove ? 0.75 : 1;
  return {
    display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', paddingLeft: 6 + v.row.depth * 14, cursor: 'default',
    whiteSpace: 'nowrap', opacity: dim,
    background: v.selected ? 'var(--accent-bg, #3a3220)' : 'transparent', color: v.selected ? 'var(--accent, #f2b544)' : 'inherit',
  };
}

function Fold({ row, collapsed, onToggle }: { row: TreeRow; collapsed: boolean; onToggle: (name: string) => void }): ReactElement {
  const style: CSSProperties = { width: 10, display: 'inline-block', color: 'var(--muted, #8b919e)' };
  if (!row.hasChildren) return <span style={style} />;
  return (
    <span aria-label={collapsed ? 'expand' : 'collapse'} style={style}
      onClick={(e) => { e.stopPropagation(); onToggle(row.name); }} onDoubleClick={(e) => { e.stopPropagation(); }}>{collapsed ? '▸' : '▾'}</span>
  );
}

/** The eye: viewport-only visibility (never `node.hidden`); a dimmed ring when the node is hidden in the viewport. */
function Eye({ name, off, onEye }: { name: string; off: boolean; onEye: (name: string) => void }): ReactElement {
  return (
    <button type="button" aria-label={off ? 'Show in viewport' : 'Hide in viewport'} aria-pressed={off} title={off ? 'Hidden in the viewport (view only)' : 'Visible in the viewport'}
      style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', padding: '0 2px', opacity: off ? 0.6 : 0.9 }}
      onClick={(e: MouseEvent) => { e.stopPropagation(); onEye(name); }} onDoubleClick={(e) => { e.stopPropagation(); }}>{off ? '○' : '◉'}</button>
  );
}

/**
 * Click selects (Ctrl/Cmd toggles, Shift ranges), double-click frames, right click opens the menu, dragging onto
 * another row reparents; while `renaming` the name is a field.
 */
export function NodeRow({ view, actions }: { view: NodeRowView; actions: NodeRowActions }): ReactElement {
  const { row, selected, collapsed } = view;
  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    const dragged = e.dataTransfer.getData(DRAG_TYPE);
    if (dragged !== '' && dragged !== row.name) actions.onReparent(dragged, row.name);
  };
  return (
    <div
      role="treeitem" aria-selected={selected} aria-expanded={row.hasChildren ? !collapsed : undefined} data-node={row.name}
      draggable={!view.renaming} style={rowStyle(view)}
      onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, row.name); e.dataTransfer.effectAllowed = 'move'; }}
      onDragOver={(e) => { e.preventDefault(); }}
      onDrop={onDrop}
      onClick={(e) => { actions.onSelect(row.name, { toggle: e.ctrlKey || e.metaKey, range: e.shiftKey }); }}
      onDoubleClick={() => { actions.onFrame(row.name); }}
      onContextMenu={(e) => { e.preventDefault(); actions.onMenu(row.name, e.clientX, e.clientY); }}
    >
      <Fold row={row} collapsed={collapsed} onToggle={actions.onToggle} />
      {view.renaming
        ? <RenameField initial={row.name} error={view.renameError} onSubmit={(v, blur) => actions.onRename(row.name, v, blur)} onCancel={actions.onRenameCancel} />
        : <span>{row.name}</span>}
      <NodeBadges row={row} keyed={view.keyed} frame={view.frame} />
      {view.drawables.length > 0 && <span style={muted}>· {view.drawables.join(', ')}</span>}
      <Eye name={row.name} off={view.viewHidden} onEye={actions.onEye} />
    </div>
  );
}
